import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, ArrowRight } from "lucide-react";

/**
 * Founding Member urgency banner for the landing hero.
 * Shows "Only X of 100 spots left" with a claim CTA. Auto-hides once cap is reached.
 */
export default function FoundingBanner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/founding-member/status").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  if (data.remaining <= 0) return null;
  // Hide if the visitor is already a Founding Member
  if (user?.founding_member) return null;

  const percentGone = Math.max(6, Math.round((data.claimed / data.cap) * 100));
  const ctaTarget = user ? "/dashboard" : "/register?founding=1";

  return (
    <Link
      to={ctaTarget}
      data-testid="founding-banner"
      className="mb-6 inline-flex items-stretch group border border-amber-500/50 bg-gradient-to-r from-amber-500/15 via-amber-500/5 to-transparent hover:border-amber-500 hover:from-amber-500/25 transition-colors overflow-hidden"
    >
      <div className="flex items-center gap-2 bg-amber-500 text-neutral-950 px-3 py-2.5 shrink-0">
        <Crown className="w-4 h-4" strokeWidth={2.5} />
        <span className="text-[10px] uppercase tracking-widest font-black">Founding</span>
      </div>
      <div className="px-4 py-2 flex items-center gap-4">
        <div>
          <div className="flex items-baseline gap-2">
            <span
              data-testid="founding-remaining"
              className="display text-2xl md:text-3xl text-amber-500 leading-none"
            >
              Only {data.remaining}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-neutral-100 font-bold">
              of {data.cap} spots left
            </span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
            <span className="text-amber-500 font-bold">€29 once</span>
            <span className="mx-1.5 text-neutral-700">·</span>
            Lifetime Pro
            <span className="mx-1.5 text-neutral-700">·</span>
            Skip €9/€49 monthly forever
          </div>
        </div>
        <div className="hidden sm:block w-16 h-1 bg-neutral-800 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-amber-500"
            style={{ width: `${percentGone}%` }}
          />
        </div>
        <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform shrink-0" />
      </div>
    </Link>
  );
}
